<template>
  <PageLayout
    title="Frequently Asked Questions"
    subtitle="Find answers to common questions about our services, process, pricing, and more."
    :show-c-t-a="false"
  >
    <div class="flex flex-col lg:flex-row gap-8">
      <!-- Left Sidebar Navigation (Desktop) -->
      <aside class="lg:w-64 flex-shrink-0">
        <nav class="bg-gray-50 dark:bg-dark-surface rounded-lg p-6 lg:sticky lg:top-24">
          <h2 class="text-sm font-bold text-gray-900 dark:text-white uppercase tracking-wider mb-4">
            Quick Navigation
          </h2>
          <ul class="space-y-2">
            <li v-for="category in content.categories" :key="category.id">
              <button
                @click="scrollToCategory(category.id)"
                :class="[
                  'w-full text-left px-4 py-3 rounded-lg font-medium transition-all duration-200',
                  activeCategory === category.id
                    ? 'bg-primary/10 dark:bg-accent/10 text-primary dark:text-accent border-l-4 border-primary dark:border-accent'
                    : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-dark-bg hover:text-primary dark:hover:text-accent border-l-4 border-transparent'
                ]"
              >
                {{ category.title }}
              </button>
            </li>
          </ul>
        </nav>
      </aside>

      <!-- Main Content -->
      <div class="flex-1 min-w-0">
        <div class="space-y-12">
          <!-- FAQ Categories -->
          <div
            v-for="category in content.categories"
            :key="category.id"
            :id="category.id"
            class="scroll-mt-32"
          >
            <h2 class="text-3xl font-display font-bold text-gray-900 dark:text-white mb-6">
              {{ category.title }}
            </h2>

            <div class="space-y-4">
              <FAQItem
                v-for="question in category.questions"
                :key="question.id"
                :question="question.question"
                :answer="question.answer"
              />
            </div>
          </div>

          <!-- Contact CTA -->
          <div class="bg-gradient-to-br from-primary to-primary-700 dark:from-accent dark:to-accent-700 rounded-2xl p-8 md:p-12 text-center text-white mt-16">
            <h2 class="text-3xl font-display font-bold mb-4">
              Still Have Questions?
            </h2>
            <p class="text-xl opacity-90 mb-8 max-w-2xl mx-auto">
              Can't find what you're looking for? Our team is here to help with any questions about your project.
            </p>
            <div class="flex flex-col sm:flex-row gap-4 justify-center">
              <router-link to="/#contact" class="btn-secondary bg-white text-primary hover:bg-gray-100">
                Contact Us
              </router-link>
              <a href="mailto:hello@devolysis.com" class="btn-secondary border-2 border-white text-white hover:bg-white/10">
                Email Directly
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  </PageLayout>
</template>

<script setup>
import { ref, onMounted, onUnmounted } from 'vue';
import PageLayout from '@/components/layout/PageLayout.vue';
import FAQItem from '@/components/ui/FAQItem.vue';
import { faqContent } from '@/data/faq';

const content = faqContent;
const activeCategory = ref(content.categories[0]?.id || '');

const scrollToCategory = (categoryId) => {
  const element = document.getElementById(categoryId);
  if (element) {
    const offsetY = 120; // Account for fixed navbar
    const targetY = element.offsetTop - offsetY;
    window.scrollTo({
      top: targetY,
      behavior: 'smooth'
    });
  }
};

// Update active category based on scroll position
const updateActiveCategory = () => {
  const scrollPosition = window.scrollY + 200; // Offset for better UX

  for (let i = content.categories.length - 1; i >= 0; i--) {
    const category = content.categories[i];
    const element = document.getElementById(category.id);

    if (element && element.offsetTop <= scrollPosition) {
      activeCategory.value = category.id;
      break;
    }
  }
};

onMounted(() => {
  window.addEventListener('scroll', updateActiveCategory);
  updateActiveCategory(); // Set initial active category
});

onUnmounted(() => {
  window.removeEventListener('scroll', updateActiveCategory);
});
</script>
